import { deriveFindingBoxCenter } from './findingPositionGeometry.js';

// A freshly shown point cloud cannot be picked until its first nodes render from the
// new camera: ~0.6-1.3 s on Merchant, longer on slow networks. Poll until a hit, and
// only report a miss after the limit. A hit returns at once, so the limit only delays
// a genuine miss.
export const PICK_POLL_INTERVAL_MS = 150;
export const PICK_WAIT_LIMIT_MS = 10000;
const PICK_ATTEMPTS = 1 + Math.ceil(PICK_WAIT_LIMIT_MS / PICK_POLL_INTERVAL_MS);
const defaultWait = delay => new Promise(resolve => setTimeout(resolve, delay));
const isFiniteVector = value => value
  && Number.isFinite(value.x)
  && Number.isFinite(value.y)
  && Number.isFinite(value.z);

export class FindingPositionResolutionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'FindingPositionResolutionError';
    this.code = code;
  }
}

function fail(code, message) {
  throw new FindingPositionResolutionError(code, message);
}

function assertCurrent(isCurrent) {
  if (!isCurrent()) {
    fail('POSITION_REQUEST_STALE', 'The finding position request is no longer current.');
  }
}

function validateImage(image) {
  if (image?.id == null || image.id === '') {
    fail('IMAGE_UNAVAILABLE', 'The source image is unavailable.');
  }
  if (!isFiniteVector(image.forgeCameraPosition) || !isFiniteVector(image.lockedForgeDirection)) {
    fail('CAMERA_CALIBRATION_UNAVAILABLE', 'The source image has no usable 3D camera calibration.');
  }
}

function resolveCenter(finding, image) {
  try {
    return deriveFindingBoxCenter(finding, image);
  } catch (err) {
    if (err?.code) {
      throw new FindingPositionResolutionError(err.code, err.message);
    }
    throw err;
  }
}

function cameraDistance(origin, hit) {
  const distance = typeof origin.distanceTo === 'function'
    ? origin.distanceTo(hit)
    : Math.hypot(origin.x - hit.x, origin.y - hit.y, origin.z - hit.z);
  return Number.isFinite(distance) ? distance : null;
}

export async function resolveFindingPosition({
  finding,
  image,
  ensureViewer,
  ensurePointCloud,
  forgeService,
  pixelToYawPitch,
  isCurrent = () => true,
  wait = defaultWait,
}) {
  assertCurrent(isCurrent);
  validateImage(image);
  const center = resolveCenter(finding, image);

  assertCurrent(isCurrent);
  const viewer = await ensureViewer();
  assertCurrent(isCurrent);
  if (!viewer) {
    fail('VIEWER_UNAVAILABLE', 'The 3D viewer is unavailable.');
  }

  assertCurrent(isCurrent);
  const cloud = await ensurePointCloud();
  assertCurrent(isCurrent);
  if (!cloud?.pcName) {
    fail('POINT_CLOUD_UNAVAILABLE', 'No point cloud is available for this scan.');
  }

  const origin = typeof image.getCameraPositionInViewer === 'function'
    ? image.getCameraPositionInViewer()
    : image.forgeCameraPosition;
  if (!isFiniteVector(origin)) {
    fail('CAMERA_CALIBRATION_UNAVAILABLE', 'The source image has no usable viewer camera position.');
  }
  const angles = pixelToYawPitch(center.x, center.y, Number(image.width), Number(image.height));
  const target = forgeService.imageYawPitchToPoint3D(image, angles.yaw, angles.pitch);
  const mainExtension = forgeService.getMainExtension();

  assertCurrent(isCurrent);
  viewer.navigation.setPosition(origin);
  viewer.navigation.setTarget(target);
  // Level the camera like the 360 link does: a fresh viewer still carries its tilted
  // home-view up vector, which would roll the preview.
  viewer.navigation.setCameraUpVector(viewer.navigation.getWorldUpVector());

  for (let attempt = 0; attempt < PICK_ATTEMPTS; attempt += 1) {
    assertCurrent(isCurrent);
    if (attempt > 0) {
      await wait(PICK_POLL_INTERVAL_MS);
      assertCurrent(isCurrent);
    }
    viewer.impl.invalidate(true, true, true);
    const client = viewer.worldToClient(target);
    const rect = viewer.impl.canvas.getBoundingClientRect();
    const hit = mainExtension.getMouseIntersectionPointCloud(
      client.x + rect.left,
      client.y + rect.top,
    );
    if (isFiniteVector(hit)) {
      const distance = cameraDistance(origin, hit);
      if (distance != null) {
        return {
          point3d: { x: hit.x, y: hit.y, z: hit.z },
          imageId: image.id,
          targetPixel: center,
          cameraDistance: distance,
        };
      }
    }
  }

  fail('POINT_CLOUD_RAY_MISS', 'No point-cloud surface was found in the finding direction.');
}
